import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import Portal from "components/layout/Portal";
import usePresence from "hooks/usePresence";
import useHardwareGps from "hooks/useHardwareGps";
import { ReactComponent as ReturnIcon } from "assets/svgs/navbar/return.svg";
import { ReactComponent as StartRecordIcon } from "assets/svgs/navbar/startrecord.svg";
import { ReactComponent as StartRecordPhotoIcon } from "assets/svgs/navbar/startrecord_photo.svg";
import { ReactComponent as StopRecordIcon } from "assets/svgs/navbar/stoprecord.svg";
import {
    fetchRecordStatus,
    getRecorderBaseUrl,
    postPhotoCapture,
    postRecordStart,
    waitForPhotoCapture,
    postRecordStop,
    type PhotoCaptureGps,
    type RecordingStatus,
} from "lib/recorderApi";
import { useARC } from "providers/ARC/ARCProvider";
import { useToast } from "providers/ToastProvider";
import useIsHttpSecure from "lib/utils/useIsHttpSecure";
import useFleetArcId from "lib/utils/useFleetArcId";
import { twMerge } from "tailwind-merge";

/** Pixels from viewport bottom to ControlPage content bottom — matches BottomBar `min-h-[3.75rem]`. */
function viewportBottomToControlContentBottomPx(): number {
    if (typeof document === "undefined") return 60;
    const rem = parseFloat(getComputedStyle(document.documentElement).fontSize) || 16;
    return rem * 3.75;
}

/** Gap between floating panel bottom and top of BottomBar. */
const FLOAT_GAP_ABOVE_NAV_PX = 8;

/**
 * Initial position on ControlPage (see ControlPage.jsx):
 * - Vertical: align panel with the Height/Speed stack
 * - Horizontal: center of the gap between the slider column and the right joystick
 */
function getDefaultRecordingsFloatPos(): { x: number; y: number } {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const margin = 16;
    const controlBottomFromViewportTop = vh - viewportBottomToControlContentBottomPx();
    const maxY = vh - FLOAT_GAP_ABOVE_NAV_PX - viewportBottomToControlContentBottomPx();
    const maxPanelW = Math.min(vw * 0.92 * 0.64, 36 * 16 * 0.64);
    const estimatedPanelW = Math.min(360 * 0.64, maxPanelW);
    const estimatedPanelH = Math.round(172 * 0.64);

    const sliderBottomPx = 8;
    const sliderBlockHeightPx = 24 + 20 + 24;
    const sliderCenterY =
        controlBottomFromViewportTop - sliderBottomPx - sliderBlockHeightPx / 2;
    const y = Math.max(
        margin,
        Math.min(
            Math.round(sliderCenterY - estimatedPanelH / 2),
            maxY - estimatedPanelH
        )
    );

    const sliderColumnRight = vw / 2 + 120;
    const joyLeftEdge = vw - 220;
    const gapStart = sliderColumnRight + 8;
    const gapEnd = Math.max(gapStart + 48, joyLeftEdge);
    const gapMidX =
        gapStart < gapEnd ? (gapStart + gapEnd) / 2 : (vw - estimatedPanelW) / 2;
    const x = Math.max(
        margin,
        Math.min(Math.round(gapMidX - estimatedPanelW / 2), vw - estimatedPanelW - margin)
    );

    return { x, y };
}

interface RecordingsPopupProps {
    visible: boolean;
    onClose: () => void;
}

const RecordingsPopup: React.FC<RecordingsPopupProps> = ({ visible, onClose }) => {
    const { mounted, state: presenceState } = usePresence(visible);
    const { selectedARC } = useARC();
    const { addToast } = useToast();
    const isSecure = useIsHttpSecure();
    const arcId = useFleetArcId();
    const gpsFix = useHardwareGps();
    const [status, setStatus] = useState<RecordingStatus | null>(null);
    const [statusError, setStatusError] = useState<string | null>(null);
    const [optimisticRecording, setOptimisticRecording] = useState<boolean | null>(null);
    const [actionPending, setActionPending] = useState(false);
    const [photoPending, setPhotoPending] = useState(false);
    const statusGenerationRef = useRef(0);

    const panelRef = useRef<HTMLDivElement>(null);
    const [floatPos, setFloatPos] = useState({ x: 0, y: 0 });

    const baseUrl = getRecorderBaseUrl(selectedARC?.host, isSecure, arcId);

    const loadStatus = useCallback(async () => {
        if (!baseUrl) return;
        const genAtStart = statusGenerationRef.current;
        try {
            const s = await fetchRecordStatus(baseUrl);
            if (genAtStart !== statusGenerationRef.current) {
                return;
            }
            setStatus(s);
            setStatusError(null);
        } catch (e) {
            if (genAtStart !== statusGenerationRef.current) {
                return;
            }
            setStatus(null);
            setStatusError(e instanceof Error ? e.message : "Status unavailable");
        }
    }, [baseUrl]);

    useEffect(() => {
        if (visible) {
            statusGenerationRef.current += 1;
            void loadStatus();
            setFloatPos(getDefaultRecordingsFloatPos());
        } else {
            setOptimisticRecording(null);
        }
    }, [visible, loadStatus]);

    useEffect(() => {
        if (!visible || !baseUrl) return;
        const t = window.setInterval(() => {
            void loadStatus();
        }, 2000);
        return () => window.clearInterval(t);
    }, [visible, baseUrl, loadStatus]);

    useEffect(() => {
        if (optimisticRecording !== true || status == null) return;
        if (status.active) {
            setOptimisticRecording(null);
        }
    }, [status, optimisticRecording]);

    useEffect(() => {
        if (optimisticRecording !== true || !status) return;
        if (!status.active && status.error) {
            setOptimisticRecording(null);
        }
    }, [status, optimisticRecording]);

    useEffect(() => {
        if (optimisticRecording !== false || status == null) return;
        if (!status.active) {
            setOptimisticRecording(null);
        }
    }, [status, optimisticRecording]);

    const recordingActive =
        optimisticRecording !== null ? optimisticRecording : Boolean(status?.active);

    const handleRecordToggle = async () => {
        if (!baseUrl || actionPending) return;
        if (recordingActive) {
            setActionPending(true);
            try {
                const next = await postRecordStop(baseUrl);
                setStatus(next);
                setStatusError(null);
                setOptimisticRecording(null);
                statusGenerationRef.current += 1;
                addToast({ type: "success", message: "Recording saved." });
            } catch (e) {
                setOptimisticRecording(null);
                addToast({
                    type: "error",
                    message: e instanceof Error ? e.message : "Recorder request failed",
                });
            } finally {
                setActionPending(false);
            }
            return;
        }

        setOptimisticRecording(true);
        setActionPending(true);
        try {
            const next = await postRecordStart(baseUrl);
            setStatus(next);
            setStatusError(null);
            setOptimisticRecording(null);
            statusGenerationRef.current += 1;
            addToast({ type: "success", message: "Recording started." });
        } catch (e) {
            setOptimisticRecording(null);
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Recorder request failed",
            });
        } finally {
            setActionPending(false);
        }
    };

    const handleTakePhoto = async () => {
        if (!baseUrl || photoPending) return;
        setPhotoPending(true);
        let gpsPayload: PhotoCaptureGps | null = null;
        if (
            gpsFix?.hasFix &&
            Number.isFinite(gpsFix.latitude) &&
            Number.isFinite(gpsFix.longitude)
        ) {
            gpsPayload = {
                latitude_deg: gpsFix.latitude as number,
                longitude_deg: gpsFix.longitude as number,
                fix: gpsFix.fix ?? 1,
            };
            if (Number.isFinite(gpsFix.altitude)) {
                gpsPayload.altitude_m = gpsFix.altitude;
            }
            if (Number.isFinite(gpsFix.satellites)) {
                gpsPayload.satellites = gpsFix.satellites;
            }
        }
        try {
            await postPhotoCapture(baseUrl, null, gpsPayload);
        } catch (e) {
            addToast({
                type: "error",
                message: e instanceof Error ? e.message : "Photo capture failed",
            });
            return;
        } finally {
            setPhotoPending(false);
        }
        addToast({ type: "info", message: "Capturing photo…" });
        void (async () => {
            try {
                const r = await waitForPhotoCapture(baseUrl);
                if (r.ok) {
                    addToast({ type: "success", message: "Photo saved." });
                } else {
                    addToast({
                        type: "error",
                        message: r.error || "Photo capture failed",
                    });
                }
            } catch (err) {
                addToast({
                    type: "error",
                    message:
                        err instanceof Error ? err.message : "Photo status request failed",
                });
            }
        })();
    };

    const onDragHandlePointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        const originX = floatPos.x;
        const originY = floatPos.y;
        const startClientX = e.clientX;
        const startClientY = e.clientY;

        const onMove = (ev: PointerEvent) => {
            const nx = originX + (ev.clientX - startClientX);
            const ny = originY + (ev.clientY - startClientY);
            const el = panelRef.current;
            const w = el?.offsetWidth ?? 320;
            const h = el?.offsetHeight ?? 120;
            const maxX = Math.max(8, window.innerWidth - w - 8);
            const maxY = Math.max(8, window.innerHeight - h - 8);
            setFloatPos({
                x: Math.min(Math.max(8, nx), maxX),
                y: Math.min(Math.max(8, ny), maxY),
            });
        };

        const onUp = () => {
            window.removeEventListener("pointermove", onMove);
            window.removeEventListener("pointerup", onUp);
            window.removeEventListener("pointercancel", onUp);
        };

        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
        window.addEventListener("pointercancel", onUp);
    };

    const iconBtnBase =
        "box-border inline-flex h-12 w-12 shrink-0 items-center justify-center rounded-r5 border p-2 transition-colors disabled:opacity-50";

    if (!mounted) {
        return null;
    }

    return (
        <Portal visible={true}>
            <div
                ref={panelRef}
                className="ios-float-panel pointer-events-auto fixed z-[100] flex min-w-[7.68rem] max-w-[min(58.88vw,23.04rem)] flex-col rounded-r10 border border-black80 bg-black90 shadow-lg"
                data-state={presenceState}
                style={{ left: floatPos.x, top: floatPos.y }}
            >
                <div
                    className="flex cursor-grab touch-none select-none items-center gap-2 border-b border-black80 bg-black100/80 px-3 py-2 active:cursor-grabbing"
                    onPointerDown={onDragHandlePointerDown}
                >
                    <span className="text-h5 min-w-0 flex-1 font-semibold text-white">
                        Recordings
                    </span>
                    <button
                        type="button"
                        aria-label="Close"
                        className="flex h-9 w-9 shrink-0 cursor-pointer items-center justify-center rounded-r5 text-black60 hover:bg-black80 hover:text-white"
                        onClick={(e) => {
                            e.stopPropagation();
                            onClose();
                        }}
                        onPointerDown={(e) => e.stopPropagation()}
                    >
                        <ReturnIcon
                            className="h-5 w-5 [&_path]:fill-current"
                            style={{ width: "1.25rem", height: "1.25rem" }}
                        />
                    </button>
                </div>

                <div className="flex flex-wrap items-center gap-2 p-3">
                    <button
                        type="button"
                        className={twMerge(
                            iconBtnBase,
                            "text-red-500 hover:text-red-400",
                            recordingActive
                                ? "border-red-500/60 bg-red-950/40 hover:border-red-500/80 hover:bg-red-950/55"
                                : "border-black80 bg-black80 hover:border-red-500/40 hover:bg-black70"
                        )}
                        onClick={() => void handleRecordToggle()}
                        disabled={!baseUrl || actionPending}
                        aria-label={
                            recordingActive ? "Stop and save recording" : "Start recording"
                        }
                    >
                        {recordingActive ? (
                            <StopRecordIcon
                                className="h-8 w-8 shrink-0"
                                style={{ width: "2rem", height: "2rem" }}
                                aria-hidden
                            />
                        ) : (
                            <StartRecordIcon
                                className="h-8 w-8 shrink-0"
                                style={{ width: "2rem", height: "2rem" }}
                                aria-hidden
                            />
                        )}
                    </button>
                    <button
                        type="button"
                        className={twMerge(
                            iconBtnBase,
                            "border-black80 bg-black80 text-white hover:border-black60 hover:bg-black70"
                        )}
                        onClick={() => void handleTakePhoto()}
                        disabled={!baseUrl || photoPending}
                        aria-label="Take photo"
                    >
                        <StartRecordPhotoIcon
                            className="h-8 w-8 shrink-0"
                            style={{ width: "2rem", height: "2rem" }}
                            aria-hidden
                        />
                    </button>
                </div>

                {(recordingActive || statusError) && (
                    <div className="space-y-1 border-t border-black80 px-3 pb-2">
                        {recordingActive && (
                            <p className="text-h5 text-red-400/90">Recording in progress…</p>
                        )}
                        {statusError && (
                            <p className="text-h5 text-red-400">{statusError}</p>
                        )}
                    </div>
                )}
            </div>
        </Portal>
    );
};

export default RecordingsPopup;
